import { useEffect, useMemo, useRef, useState } from 'react'

import { useCartStore } from '@features/cart/model/useCartStore'

import { Product } from '@shared/generated'
import { cn } from '@shared/lib/cn'

import EmptyCart from './empty'
import CartFooter from './footer'
import CartHeader from './header'
import CartListPreview from './list'
import CartLoading from './loading'

import styles from './styles.module.scss'

interface CartPreviewProps {
  onClose?: () => void;
}

export default function CartPreview ({ onClose }: CartPreviewProps) {
  const {
    getCart,
    isSyncing,
    cartInfoData: cartTotal,
    cartInfoLoading: loading,
  } = useCartStore()

  const cart = getCart()
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [ hasScroll, setHasScroll ] = useState(false)

  const hasItems = useMemo(() => {
    // Prioritize server data (cartTotal) over local cart data
    const hasServerProducts = !!(
      (cartTotal?.products && cartTotal.products.length > 0)
      || (cartTotal?.saleAddedPositions && cartTotal.saleAddedPositions.length > 0)
    )
    const hasLocalItems = !!cart?.items.length

    return hasServerProducts || hasLocalItems
  }, [ cart, cartTotal ])
  const isLoading = useMemo(() => {
    // Don't show empty state while loading
    return (loading && !cartTotal) || isSyncing
  }, [ loading, isSyncing, cartTotal ])
  const productsData = useMemo(() => {
    return cartTotal?.products?.flatMap((position) => {
      if (!position?.info?.id) return []

      return [ position.info ]
    }) ?? []
  }, [ cartTotal ])

  useEffect(() => {
    if (scrollAreaRef.current) {
      const hasVerticalScroll =
        scrollAreaRef.current.scrollHeight > scrollAreaRef.current.clientHeight
      setHasScroll(hasVerticalScroll)
    }
  }, [ productsData ])

  return (
    <div
      className={cn(styles.root, {
        [styles.products]: hasItems,
        [styles.empty]: !hasItems,
      })}
    >
      {!hasItems && !isLoading ? (
        <EmptyCart />
      ) : hasItems ? (
        <>
          {!isLoading && <CartHeader onClose={onClose} />}

          <div
            ref={scrollAreaRef}
            className={cn(styles.scrollArea, { [styles.hasScroll]: hasScroll })}
          >
            {!isLoading && productsData.length > 0 ? (
              <CartListPreview data={productsData as Product[]} />
            ) : isLoading ? (
              <CartLoading />
            ) : null}
          </div>

          {cartTotal && <CartFooter />}
        </>
      ) : (
        <CartLoading />
      )}
    </div>
  )
}
